import apiClient from "@/api/client";

import type {
  AskResult,
  BenchmarksData,
  BenchmarksParams,
  BrowserRun,
  CandidatesPreview,
  EzConfigDto,
  FeedMeta,
  FeedParams,
  IngestWindowRow,
  InsightRow,
  ProceedingDetail,
  ProceedingRow,
  SimilarAnalysis,
  TenderDocuments,
  UserDecision,
} from "./types";

const BASE = "/ezamowienia";

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

export const getFeed = async (params: FeedParams) => {
  const r = await apiClient.get(`${BASE}/feed/`, { params });
  return { rows: r.data.data as ProceedingRow[], meta: r.data.meta as FeedMeta };
};

export const getProceeding = (id: string) =>
  unwrap<ProceedingDetail>(apiClient.get(`${BASE}/proceedings/${id}/`));

export const postEstimate = (id: string, documentId: string, fileName: string) =>
  unwrap<{ task_id: string; job_id: string; files: number }>(
    apiClient.post(`${BASE}/proceedings/${id}/estimate/`, {
      document_id: documentId,
      file_name: fileName,
    }),
  );

export const getDocuments = (id: string) =>
  unwrap<TenderDocuments>(apiClient.get(`${BASE}/proceedings/${id}/documents/`));

export const postRecommend = (id: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/proceedings/${id}/recommend/`, {}),
  );

export const postBrowserFetch = (id: string) =>
  unwrap<{ task_id: string; run: BrowserRun }>(
    apiClient.post(`${BASE}/proceedings/${id}/browser-fetch/`, {}),
  );

export const getBrowserRuns = (id: string) =>
  unwrap<BrowserRun[]>(apiClient.get(`${BASE}/proceedings/${id}/browser-runs/`));

export const getSimilar = (id: string) =>
  unwrap<SimilarAnalysis>(apiClient.get(`${BASE}/proceedings/${id}/similar/`));

export const postDecision = (id: string, decision: UserDecision) =>
  unwrap<ProceedingRow>(
    apiClient.post(`${BASE}/proceedings/${id}/decision/`, { decision }),
  );

export const postAsk = (
  question: string,
  history: { role: string; content: string }[],
  model?: string,
) => unwrap<AskResult>(apiClient.post(`${BASE}/ask/`, { question, history, model }));

export const getInsights = () => unwrap<InsightRow[]>(apiClient.get(`${BASE}/insights/`));

export const postInsight = (params: BenchmarksParams) =>
  unwrap<InsightRow>(apiClient.post(`${BASE}/insights/`, params));

export const getBenchmarks = (params: BenchmarksParams) =>
  unwrap<BenchmarksData>(apiClient.get(`${BASE}/benchmarks/`, { params }));

export const getCandidatesPreview = () =>
  unwrap<CandidatesPreview>(apiClient.get(`${BASE}/candidates/preview/`));

export const postAnalyze = (body: { ids?: string[]; scope?: "open" | "all" }) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/candidates/analyze/`, body));

export const postHistoricalLoad = (body: {
  results_months: number;
  contracts_months: number;
}) => unwrap<{ task_id: string }>(apiClient.post(`${BASE}/historical-load/`, body));

export const getIngestWindows = () =>
  unwrap<IngestWindowRow[]>(apiClient.get(`${BASE}/ingest-windows/`));

export const getConfig = () => unwrap<EzConfigDto>(apiClient.get(`${BASE}/config/`));

export const putConfig = (body: Partial<EzConfigDto>) =>
  unwrap<EzConfigDto>(apiClient.put(`${BASE}/config/`, body));
